import { useState, useEffect } from 'react';

const SIDEBAR_STATE_KEY = 'learnhub-sidebar-state';

interface SidebarState {
  isCollapsed: boolean;
  expandedGroups: string[];
}

const defaultState: SidebarState = {
  isCollapsed: false,
  expandedGroups: ['main-navigation', 'course-navigation']
};

export const useSidebarPersistence = () => {
  const [sidebarState, setSidebarState] = useState<SidebarState>(defaultState);

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
      if (saved) {
        const parsedState = JSON.parse(saved);
        setSidebarState({ ...defaultState, ...parsedState });
      }
    } catch (error) {
      console.warn('Failed to load sidebar state from localStorage:', error);
    }
  }, []);

  // Save state to localStorage whenever it changes
  const updateSidebarState = (updates: Partial<SidebarState>) => {
    const newState = { ...sidebarState, ...updates };
    setSidebarState(newState);
    
    try {
      localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(newState));
    } catch (error) {
      console.warn('Failed to save sidebar state to localStorage:', error);
    }
  };

  const toggleCollapsed = () => {
    updateSidebarState({ isCollapsed: !sidebarState.isCollapsed });
  };

  const toggleGroupExpanded = (groupId: string) => {
    const expandedGroups = sidebarState.expandedGroups.includes(groupId)
      ? sidebarState.expandedGroups.filter(id => id !== groupId)
      : [...sidebarState.expandedGroups, groupId];
    
    updateSidebarState({ expandedGroups });
  };

  const isGroupExpanded = (groupId: string) => {
    return sidebarState.expandedGroups.includes(groupId);
  };

  return {
    isCollapsed: sidebarState.isCollapsed,
    expandedGroups: sidebarState.expandedGroups,
    toggleCollapsed,
    toggleGroupExpanded,
    isGroupExpanded,
    setSidebarState: updateSidebarState
  };
};